const STORAGE_KEY = "recent-searches";
const DB_NAME = "travelwings-ui";
const STORE_NAME = "app-state";
const RECORD_KEY = "recent-flight-searches";
const MAX_RECENT_SEARCHES = 6;
const COOKIE_MAX_AGE_SECONDS = 60 * 60 * 24 * 365;

let memoryRecentSearches = [];

const safeJsonParse = (value, fallback = []) => {
	try {
		return value ? JSON.parse(value) : fallback;
	} catch (error) {
		return fallback;
	}
};

const isBrowser = () => typeof window !== "undefined";

const supportsIndexedDb = () => isBrowser() && typeof window.indexedDB !== "undefined";

const supportsLocalStorage = () => {
	if (!isBrowser()) return false;
	try {
		const probeKey = "__tw_recent_searches__";
		window.localStorage.setItem(probeKey, "1");
		window.localStorage.removeItem(probeKey);
		return true;
	} catch (error) {
		return false;
	}
};

const supportsCookies = () => {
	if (!isBrowser()) return false;
	try {
		document.cookie = "tw_recent_searches_probe=1; path=/; SameSite=Lax";
		const supported = document.cookie.includes("tw_recent_searches_probe=1");
		document.cookie = "tw_recent_searches_probe=; Max-Age=0; path=/; SameSite=Lax";
		return supported;
	} catch (error) {
		return false;
	}
};

const getCookieValue = (name) => {
	if (!isBrowser()) return "";
	const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
	return match ? decodeURIComponent(match[1]) : "";
};

const setCookieValue = (name, value) => {
	if (!supportsCookies()) return;
	document.cookie = `${name}=${encodeURIComponent(value)}; Max-Age=${COOKIE_MAX_AGE_SECONDS}; path=/; SameSite=Lax`;
};

const normalizeAirport = (airport = {}) => ({
	airportCode: airport?.airportCode || "",
	airportName: airport?.airportName || "",
	cityName: airport?.cityName || "",
});

const buildLabel = ({ from, to }) => {
	const fromLabel = from?.cityName || from?.airportCode || "From";
	const toLabel = to?.cityName || to?.airportCode || "To";
	return `${fromLabel.toUpperCase()} to ${toLabel.toUpperCase()}`;
};

const createSignature = (search = {}) => {
	const passengers = search?.passengers || {};
	const range = search?.range || {};
	return [
		search?.mode?.value || "oneway",
		search?.from?.airportCode || search?.from?.cityName || "",
		search?.to?.airportCode || search?.to?.cityName || "",
		search?.date || "",
		range?.startDate || "",
		range?.endDate || "",
		passengers?.adult ?? 1,
		passengers?.child ?? 0,
		passengers?.infant ?? 0,
		search?.cabinClass?.value || "",
		search?.optimize === false ? "0" : "1",
	].join("|");
};

const normalizeRecentSearch = (search = {}, fallbackOrder = 0) => {
	const source = search?.value ? search.value : search;
	const normalized = {
		label: search?.label || buildLabel(source),
		from: normalizeAirport(source?.from),
		to: normalizeAirport(source?.to),
		mode: source?.mode ? { label: source.mode.label, value: source.mode.value } : { label: "One Way", value: "oneway" },
		date: source?.date || null,
		range: source?.range ? { startDate: source.range.startDate || null, endDate: source.range.endDate || null } : null,
		passengers: {
			adult: Number(source?.passengers?.adult ?? 1),
			child: Number(source?.passengers?.child ?? 0),
			infant: Number(source?.passengers?.infant ?? 0),
		},
		cabinClass: source?.cabinClass ? { label: source.cabinClass.label, value: source.cabinClass.value } : null,
		optimize: source?.optimize !== false,
		updatedAt: search?.updatedAt || Date.now() - fallbackOrder,
	};
	return {
		...normalized,
		signature: search?.signature || createSignature(normalized),
	};
};

const sortRecentSearches = (searches = []) => {
	return [...searches].sort((prev, next) => (next.updatedAt || 0) - (prev.updatedAt || 0));
};

const getStartOfToday = () => {
	const today = new Date();
	today.setHours(0, 0, 0, 0);
	return today.getTime();
};

const isFutureSearch = (search = {}) => {
	const tripDate = search?.mode?.value === "round" ? search?.range?.startDate : search?.date;
	if (!tripDate) return true;
	return Number(tripDate) >= getStartOfToday();
};

const normalizeRecentSearches = (searches = []) => {
	if (!Array.isArray(searches)) return [];
	const orderedInput = searches.some((search) => search?.updatedAt) ? searches : [...searches].reverse();
	const normalized = orderedInput
		.map((search, index) => normalizeRecentSearch(search, index))
		.filter((search) => search.from.airportCode && search.to.airportCode)
		.filter(isFutureSearch)
		.filter((search, index, self) => index === self.findIndex((candidate) => candidate.signature === search.signature));
	return sortRecentSearches(normalized).slice(0, MAX_RECENT_SEARCHES);
};

const openDb = () =>
	new Promise((resolve, reject) => {
		if (!supportsIndexedDb()) {
			reject(new Error("IndexedDB not supported"));
			return;
		}
		const request = window.indexedDB.open(DB_NAME, 1);
		request.onupgradeneeded = () => {
			const db = request.result;
			if (!db.objectStoreNames.contains(STORE_NAME)) {
				db.createObjectStore(STORE_NAME);
			}
		};
		request.onsuccess = () => resolve(request.result);
		request.onerror = () => reject(request.error || new Error("IndexedDB open failed"));
	});

const readFromIndexedDb = async () => {
	try {
		const db = await openDb();
		return await new Promise((resolve, reject) => {
			const transaction = db.transaction(STORE_NAME, "readonly");
			const store = transaction.objectStore(STORE_NAME);
			const request = store.get(RECORD_KEY);
			request.onsuccess = () => resolve(safeJsonParse(request.result, []));
			request.onerror = () => reject(request.error || new Error("IndexedDB read failed"));
		});
	} catch (error) {
		return [];
	}
};

const writeToIndexedDb = async (searches) => {
	try {
		const db = await openDb();
		await new Promise((resolve, reject) => {
			const transaction = db.transaction(STORE_NAME, "readwrite");
			const store = transaction.objectStore(STORE_NAME);
			store.put(JSON.stringify(searches), RECORD_KEY);
			transaction.oncomplete = () => resolve(true);
			transaction.onerror = () => reject(transaction.error || new Error("IndexedDB write failed"));
		});
	} catch (error) {
		return false;
	}
	return true;
};

const readFromLocalStorage = () => {
	if (!supportsLocalStorage()) return [];
	return safeJsonParse(window.localStorage.getItem(STORAGE_KEY), []);
};

const writeToLocalStorage = (searches) => {
	if (!supportsLocalStorage()) return false;
	try {
		window.localStorage.setItem(STORAGE_KEY, JSON.stringify(searches));
		return true;
	} catch (error) {
		return false;
	}
};

const readFromCookie = () => {
	if (!supportsCookies()) return [];
	return safeJsonParse(getCookieValue(STORAGE_KEY), []);
};

const writeToCookie = (searches) => {
	if (!supportsCookies()) return false;
	try {
		setCookieValue(STORAGE_KEY, JSON.stringify(searches));
		return true;
	} catch (error) {
		return false;
	}
};

const persistRecentSearches = async (searches) => {
	const normalized = normalizeRecentSearches(searches);
	memoryRecentSearches = normalized;
	await writeToIndexedDb(normalized);
	writeToLocalStorage(normalized);
	writeToCookie(normalized);
	return normalized;
};

export const getRecentFlightSearches = async () => {
	const indexedDbRawSearches = await readFromIndexedDb();
	const indexedDbSearches = normalizeRecentSearches(indexedDbRawSearches);
	if (indexedDbRawSearches.length) {
		if (indexedDbSearches.length !== indexedDbRawSearches.length) {
			return persistRecentSearches(indexedDbSearches);
		}
		memoryRecentSearches = indexedDbSearches;
		return indexedDbSearches;
	}

	const localStorageRawSearches = readFromLocalStorage();
	const localStorageSearches = normalizeRecentSearches(localStorageRawSearches);
	if (localStorageRawSearches.length) {
		return persistRecentSearches(localStorageSearches);
	}

	const cookieRawSearches = readFromCookie();
	const cookieSearches = normalizeRecentSearches(cookieRawSearches);
	if (cookieRawSearches.length) {
		return persistRecentSearches(cookieSearches);
	}

	return normalizeRecentSearches(memoryRecentSearches);
};

export const saveRecentFlightSearch = async ({ values, optimize }) => {
	const nextSearch = normalizeRecentSearch({
		label: buildLabel(values),
		...values,
		optimize,
		updatedAt: Date.now(),
	});
	const currentSearches = await getRecentFlightSearches();
	const updatedSearches = [nextSearch, ...currentSearches.filter((search) => search.signature !== nextSearch.signature)];
	return persistRecentSearches(updatedSearches);
};
